import '.ba{user-select:none;}.ssr-plugin'
import '.eb{flex-shrink:0;}.ssr-plugin'
import '.b9{appearance:none;}.ssr-plugin'
import '.f3{outline:none;}.ssr-plugin'
import '.am{overflow:hidden;}.ssr-plugin'
import '.a2{border:none;}.ssr-plugin'
import '.s{position:relative;}.ssr-plugin'
import '.eh{display:inline-flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.f7{transition:transform 200ms cubic-bezier(0.2,0,0,1);}.ssr-plugin'
import '.u{position:absolute;}.ssr-plugin'
import '.al{inset:0px;}.ssr-plugin'
import '.a1{pointer-events:none;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.r{flex:1;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.f8{padding-left:2px;}.ssr-plugin'
import '.f9{padding-right:2px;}.ssr-plugin'
import '.fa{margin-right:3px;}.ssr-plugin'
import '.fb{margin-left:3px;}.ssr-plugin'
import { ReactNode, useState, forwardRef, Fragment, memo } from 'react'
import { radius } from '../../utils/radius.js'
import { Icon, IconProps } from '../Icon/index.js'
import { Text } from '../Text/index.js'
import { colors } from '../../tokens/colors.js'
import { KeyHint, KeyHintProps } from '../KeyHint/index.js'
import { styled, Style } from 'inlines'
import { Tooltip } from '../Tooltip/index.js'
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut.js'
import { Loader } from '../Loader/index.js'
import { Counter } from '../Counter/index.js'

type ButtonProps = {
  disabled?: boolean
  variant?: 'fill' | 'border' | 'ghost'
  color?: 'neutral' | 'red'
  size?: 'regular' | 'small' | 'tiny'
  align?: 'left' | 'center' | 'right'
  trailIcon?: IconProps['variant']
  loading?: boolean
  toggled?: boolean
  singleLine?: boolean
  keyHint?: KeyHintProps['hint']
  keyHintVisibility?: 'label' | 'tooltip' | 'none'
  tooltip?: string
  onClick?: (e?: React.MouseEvent) => void | Promise<void>
  onFocus?: () => void
  onBlur?: () => void
  width?: 'auto' | 'full'
  forceHover?: boolean
  counter?: number
  style?: Style
} & (
  | { leadIcon: IconProps['variant']; children?: ReactNode }
  | { leadIcon?: never; children: ReactNode }
)

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      disabled,
      variant: variantProp = 'fill',
      color = 'neutral',
      size = 'regular',
      align,
      leadIcon,
      trailIcon,
      loading: loadingProp,
      toggled,
      singleLine = true,
      keyHint,
      tooltip,
      keyHintVisibility = 'label',
      width = 'auto',
      forceHover,
      counter,
      onClick,
      onFocus,
      onBlur,
      style,
    },
    ref,
  ) => {
    const [internalLoading, setInternalLoading] = useState(false)
    const [shaking, setShaking] = useState(false)

    const loading = internalLoading || loadingProp
    const variant = toggled ? 'fill' : variantProp

    async function handleClick(e: React.MouseEvent) {
      if (disabled) return

      try {
        const result = onClick?.(e)

        if (result instanceof Promise) {
          setInternalLoading(true)
          await result
        }
      } catch (err) {
        setShaking(true)
        console.error(err)
      } finally {
        setInternalLoading(false)
      }
    }

    useKeyboardShortcut(keyHint, handleClick)

    const Wrapper =
      tooltip || (keyHint && keyHintVisibility === 'tooltip')
        ? Tooltip
        : Fragment

    const wrapperProps =
      tooltip || (keyHint && keyHintVisibility === 'tooltip')
        ? {
            description: tooltip,
            keyHint: keyHintVisibility === 'tooltip' ? keyHint : undefined,
          }
        : {}

    return (
      <Wrapper {...wrapperProps}>
        <styled.button className="ba eb b9 f3 am a2 s eh c d f7"
          ref={ref}
          onAnimationEnd={() => {
            setShaking(false)
          }}
          onClick={handleClick}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled || loading}
          data-size={size}
          data-variant={variant}
          data-color={color}
          data-loading={loading ? loading : undefined}
          data-disabled={disabled ? disabled : undefined}
          data-toggled={toggled ? toggled : undefined}
          data-forcehover={forceHover ? forceHover : undefined}
          style={{
            cursor: !disabled ? 'pointer' : undefined,
            width: width === 'full' ? '100%' : 'auto',
            flex: width === 'full' ? 1 : undefined,
            borderRadius:
              radius[size === 'tiny' ? 4 : size === 'small' ? 8 : 12],
            ...(shaking && {
              animation: '200ms shake ease-in-out',
            }),
            '&[data-size=regular]': {
              minHeight: 36,
              minWidth: 36,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: !children && !trailIcon ? 6 : leadIcon ? 6 : 10,
              paddingRight:
                !children && !trailIcon
                  ? 6
                  : trailIcon || keyHint || !children
                    ? 6
                    : 10,
            },
            '&[data-size=small]': {
              minHeight: 24,
              minWidth: 24,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: leadIcon ? 4 : 6,
              paddingRight: trailIcon || keyHint || !children ? 4 : 6,
            },
            '&[data-size=tiny]': {
              minHeight: 16,
              minWidth: 16,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: leadIcon ? 0 : 4,
              paddingRight: trailIcon || keyHint || !children ? 0 : 4,
            },
            // focus outline styles
            '&[data-color=neutral]:focus-visible': {
              outline: `${colors.neutral20} solid 4px`,
            },
            '&[data-color=red]:focus-visible': {
              outline: `${colors.red60} solid 4px`,
            },
            // fill variant styles
            '&[data-variant=fill][data-color=neutral]': {
              background: colors.neutral100,
              color: colors.neutralInverted100,
            },
            '&[data-variant=fill][data-color=neutral]:hover:not(:disabled) .overlay, &[data-variant=fill][data-color=neutral][data-forcehover]:not(:disabled) .overlay':
              {
                background: colors.neutralInverted20,
              },
            '&[data-variant=fill][data-color=neutral]:focus-visible .overlay': {
              background: colors.neutralInverted20,
            },
            '&[data-variant=fill][data-color=neutral]:disabled': {
              opacity: 0.2,
            },
            '&[data-variant=fill][data-color=red]': {
              background: colors.red100,
              color: colors.white100,
            },
            '&[data-variant=fill][data-color=red]:hover:not(:disabled) .overlay, &[data-variant=fill][data-color=red][data-forcehover]:not(:disabled) .overlay':
              {
                background: colors.neutral10,
              },
            '&[data-variant=fill][data-color=red]:focus-visible .overlay': {
              background: colors.neutral10,
            },
            '&[data-variant=fill][data-color=red]:disabled': {
              background: colors.red20,
              color: colors.red20,
            },
            '&[data-variant=fill][data-color=red]:disabled .keyHintOverlay': {
              opacity: 0.2,
            },
            '&[data-variant=fill][data-color=red]:disabled .loaderOverlay': {
              opacity: 0.2,
            },
            // border variant styles
            '&[data-variant=border][data-color=neutral]': {
              background: 'transparent',
              color: colors.neutral80,
              boxShadow: `inset 0 0 0 1px ${colors.neutral20Adjusted}`,
            },
            '&[data-variant=border][data-color=neutral]:hover:not(:disabled), &[data-variant=border][data-color=neutral][data-forcehover]:not(:disabled)':
              {
                color: colors.neutral100,
                background: colors.neutral10Adjusted,
                boxShadow: 'none',
              },
            '&[data-variant=border][data-color=neutral]:focus-visible': {
              color: colors.neutral100,
              background: colors.neutral10Adjusted,
              boxShadow: 'none',
            },
            '&[data-variant=border][data-color=neutral]:disabled': {
              background: 'transparent',
              boxShadow: `inset 0 0 0 1px ${colors.neutral10Adjusted}`,
              color: colors.neutral20,
            },
            '&[data-variant=border][data-color=neutral]:disabled .keyHintOverlay':
              {
                opacity: 0.2,
              },
            '&[data-variant=border][data-color=neutral]:disabled .loaderOverlay':
              {
                opacity: 0.2,
              },
            '&[data-variant=border][data-color=red]': {
              background: 'transparent',
              color: colors.red80,
              boxShadow: `inset 0 0 0 1px ${colors.red20}`,
            },
            '&[data-variant=border][data-color=red]:hover:not(:disabled), &[data-variant=border][data-color=red][data-forcehover]:not(:disabled)':
              {
                background: colors.red20,
                color: colors.red100,
                boxShadow: 'none',
              },
            '&[data-variant=border][data-color=red]:focus-visible': {
              background: colors.red20,
              color: colors.red100,
              boxShadow: 'none',
            },
            '&[data-variant=border][data-color=red]:disabled': {
              background: 'transparent',
              color: colors.red20,
              boxShadow: `inset 0 0 0 1px ${colors.red10}`,
            },
            '&[data-variant=border][data-color=red]:disabled .keyHintOverlay': {
              opacity: 0.2,
            },
            '&[data-variant=border][data-color=red]:disabled .loaderOverlay': {
              opacity: 0.2,
            },
            // ghost variant styles
            '&[data-variant=ghost][data-color=neutral]': {
              background: 'transparent',
              color: colors.neutral80,
            },
            '&[data-variant=ghost][data-color=neutral]:hover:not(:disabled), &[data-variant=ghost][data-color=neutral][data-forcehover]:not(:disabled)':
              {
                color: colors.neutral100,
                background: colors.neutral10Adjusted,
              },
            '&[data-variant=ghost][data-color=neutral]:focus-visible': {
              color: colors.neutral100,
              background: colors.neutral10Adjusted,
            },
            '&[data-variant=ghost][data-color=neutral]:disabled': {
              background: 'transparent',
              color: colors.neutral20,
            },
            '&[data-variant=ghost][data-color=neutral]:disabled .keyHintOverlay':
              {
                opacity: 0.2,
              },
            '&[data-variant=ghost][data-color=neutral]:disabled .loaderOverlay':
              {
                opacity: 0.2,
              },
            '&[data-variant=ghost][data-color=red]': {
              background: 'transparent',
              color: colors.red80,
            },
            '&[data-variant=ghost][data-color=red]:hover:not(:disabled), &[data-variant=ghost][data-color=red][data-forcehover]:not(:disabled)':
              {
                background: colors.red20,
                color: colors.red100,
              },
            '&[data-variant=ghost][data-color=red]:focus-visible': {
              background: colors.red20,
              color: colors.red100,
            },
            '&[data-variant=ghost][data-color=red]:disabled': {
              background: 'transparent',
              color: colors.red20,
            },
            '&[data-variant=ghost][data-color=red]:disabled .keyHintOverlay': {
              opacity: 0.2,
            },
            '&[data-variant=ghost][data-color=red]:disabled .loaderOverlay': {
              opacity: 0.2,
            },
            '&:active:not(:disabled)': {
              transform: 'scale(0.98)',
            },
            ...style,
          }}
        >
          <div className="overlay u al a1"
          />
          <styled.div className="s a r f d"
            data-size={size}
            style={{
              '&[data-size=regular]': {
                gap: 2,
                justifyContent:
                  align === 'center'
                    ? 'center'
                    : align === 'right'
                      ? 'flex-end'
                      : 'flex-start',
              },
              '&[data-size=small]': {
                gap: 2,
              },
              ...(leadIcon && !children
                ? {
                    gap: 0,
                  }
                : null),
            }}
          >
            {leadIcon && !loading && (
              <Icon
                size={size === 'tiny' ? 'small' : size}
                variant={leadIcon}
                scaleChevrons
                color="inherit"
              />
            )}
            {(loading && !trailIcon) ||
            (loading && leadIcon) ||
            (loading && !leadIcon && !trailIcon) ? (
              <div className="loaderOverlay a f8 f9"
              >
                <Loader
                  style={{ width: 16, height: 16 }}
                  color={
                    variant === 'fill'
                      ? color === 'red'
                        ? 'red'
                        : 'inverted'
                      : color === 'red'
                        ? 'red'
                        : 'neutral'
                  }
                />
              </div>
            ) : null}
            {typeof children === 'string' ||
            // this check fixes Safari shrinking the text
            // when children is not a simple string.
            // A more performant solution may exist
            (Array.isArray(children) &&
              children.every(
                (c) => typeof c === 'string' || typeof c === 'number',
              )) ? (
              <Text
                color="inherit"
                singleLine={singleLine}
                selectableText={false}
                variant={size === 'small' ? 'subtext-medium' : 'display-medium'}
                style={{ marginTop: 1 }} // Visual offset
              >
                {children}
              </Text>
            ) : (
              children
            )}
            {trailIcon && !loading && (
              <Icon
                size={size === 'tiny' ? 'small' : size}
                variant={trailIcon}
                color="inherit"
                style={
                  leadIcon && !children
                    ? {
                        marginLeft: -4,
                      }
                    : null
                }
              />
            )}
            {trailIcon && !leadIcon && loading && (
              <div className="loaderOverlay a">
                <Loader
                  color={
                    variant === 'fill'
                      ? color === 'red'
                        ? 'red'
                        : 'inverted'
                      : color === 'red'
                        ? 'red'
                        : 'neutral'
                  }
                />
              </div>
            )}
            {Boolean(counter) && !loading && size === 'regular' && (
              <Counter
                color={
                  color === 'red'
                    ? variant === 'fill'
                      ? 'white'
                      : 'red'
                    : variant === 'fill'
                      ? 'inverted'
                      : 'neutral'
                }
              >
                {counter}
              </Counter>
            )}
            {keyHint && keyHintVisibility === 'label' && (
              <div className="keyHintOverlay a fa fb"
              >
                <KeyHint
                  color={
                    variant === 'fill'
                      ? color === 'red'
                        ? loading || disabled
                          ? 'red'
                          : 'white'
                        : 'inverted'
                      : color === 'red'
                        ? 'red'
                        : 'neutral'
                  }
                  hint={keyHint}
                />
              </div>
            )}
          </styled.div>
        </styled.button>
      </Wrapper>
    )
  },
)

const MemoizedButton = memo(Button)

export type { ButtonProps }
export { MemoizedButton as Button }
